/**
 * Ordered semantic identities supplied by the selected view's source adapter.
 * Sources replace these arrays when membership or order changes.
 */
export interface ProjectionIds {
  readonly entryIds: readonly string[];
  readonly fieldIds: readonly string[];
}

export type ViewProjectionSnapshot =
  | { readonly status: 'unavailable' }
  | ({ readonly status: 'ready' } & ProjectionIds);

/**
 * A view-scoped placement read shared by semantic consumers, never a state store.
 * The caller disposes consumers before disposing the projection on view replacement.
 */
export interface ViewProjection {
  getSnapshot(): ViewProjectionSnapshot;
  /**
   * Notifies only after ordered identities or availability change. Value-only
   * source invalidations retain the snapshot and do not notify consumers.
   */
  subscribe(listener: VoidFunction): VoidFunction;
  dispose(): void;
}

const emptyIds: readonly string[] = Object.freeze([]);
const unavailable: ViewProjectionSnapshot = Object.freeze({ status: 'unavailable' });

function retainIds(previous: readonly string[], next: readonly string[]) {
  if (
    previous.length === next.length &&
    previous.every((id, index) => id === next[index])
  ) {
    return previous;
  }

  return Object.isFrozen(next) ? next : Object.freeze([...next]);
}

/**
 * Shares immutable placement without owning selection, presence or geometry.
 * Sources install readable state before invalidating. Stable source-array identity
 * avoids scans; replacement arrays are compared once and equal ordering is retained.
 * No source reads occur until a consumer reads or subscribes, or after disposal.
 * This opt-in factory installs no application lifecycle or renderer subscription.
 */
export function createViewProjection(source: {
  read(): ProjectionIds | undefined;
  subscribeInvalidation(listener: VoidFunction): VoidFunction;
}): ViewProjection {
  let disposed = false;
  let snapshot = unavailable;
  /**
   * Reads can precede a deferred source event; they must not consume its notification.
   */
  let published = unavailable;
  let entryIds: readonly string[] | undefined;
  let fieldIds: readonly string[] | undefined;
  const listeners = new Set<VoidFunction>();

  function getSnapshot(): ViewProjectionSnapshot {
    if (disposed) {
      return unavailable;
    }

    const next = source.read();

    if (!next) {
      entryIds = undefined;
      fieldIds = undefined;
      snapshot = unavailable;

      return snapshot;
    }

    if (
      snapshot.status === 'ready' &&
      entryIds === next.entryIds &&
      fieldIds === next.fieldIds
    ) {
      return snapshot;
    }

    const previous = snapshot.status === 'ready' ? snapshot : undefined;
    const entries =
      previous && entryIds === next.entryIds
        ? previous.entryIds
        : retainIds(previous?.entryIds ?? emptyIds, next.entryIds);
    const fields =
      previous && fieldIds === next.fieldIds
        ? previous.fieldIds
        : retainIds(previous?.fieldIds ?? emptyIds, next.fieldIds);

    entryIds = next.entryIds;
    fieldIds = next.fieldIds;

    if (!previous || entries !== previous.entryIds || fields !== previous.fieldIds) {
      snapshot = Object.freeze({ status: 'ready', entryIds: entries, fieldIds: fields });
    }

    return snapshot;
  }

  const unsubscribe = source.subscribeInvalidation(() => {
    if (disposed || listeners.size === 0) {
      return;
    }

    const next = getSnapshot();

    if (next !== published) {
      published = next;

      for (const listener of listeners) {
        listener();
      }
    }
  });

  return {
    getSnapshot,
    subscribe(listener) {
      if (disposed) {
        return () => {};
      }

      if (listeners.size === 0) {
        published = getSnapshot();
      }

      listeners.add(listener);

      return () => {
        listeners.delete(listener);
      };
    },
    dispose() {
      if (disposed) {
        return;
      }

      disposed = true;
      listeners.clear();
      snapshot = unavailable;
      published = unavailable;
      entryIds = undefined;
      fieldIds = undefined;
      unsubscribe();
    },
  };
}
